
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';

interface ProfileInvestmentPreferencesProps {
  data: any;
  updateData: (data: any) => void;
}

const ProfileInvestmentPreferences = ({ data, updateData }: ProfileInvestmentPreferencesProps) => {
  const [formData, setFormData] = useState({
    esgPreference: data.esgPreference || 3, // Scale 1-5
    sectorPreferences: data.sectorPreferences || {
      technology: false,
      healthcare: false,
      finance: false,
      consumerGoods: false,
      manufacturing: false,
      realEstate: false,
      others: false
    },
    sectorExclusions: data.sectorExclusions || [],
    geographicPreferences: data.geographicPreferences || 'india-focused',
    managementPreference: data.managementPreference || ''
  });

  useEffect(() => {
    updateData(formData);
  }, [formData, updateData]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectorToggle = (sector: string) => {
    setFormData(prev => ({
      ...prev,
      sectorPreferences: {
        ...prev.sectorPreferences,
        [sector]: !prev.sectorPreferences[sector as keyof typeof prev.sectorPreferences]
      }
    }));
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Investment Preferences</h2>
      
      <div className="space-y-8">
        {/* ESG/Sustainable Investing Preference */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>How important is sustainable/ESG investing to you?</Label>
            <span className="text-[#9E7C47] text-sm">
              {formData.esgPreference}/5
            </span>
          </div>
          
          <Slider
            min={1}
            max={5}
            step={1}
            value={[formData.esgPreference]}
            onValueChange={(value) => handleChange('esgPreference', value[0])}
            className="py-4"
          />
          
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>Not important</span>
            <span>Somewhat important</span>
            <span>Very important</span>
          </div>
          
          <p className="text-xs text-white/70 mt-2">
            Environmental, Social, and Governance (ESG) investing focuses on companies with positive environmental and social impact.
          </p>
        </div>
        
        {/* Sector Preferences */}
        <div>
          <Label className="mb-3 block">Sector Preferences</Label>
          
          <Card className="bg-[#9E7C47]/5 border-[#9E7C47]/20">
            <CardContent className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { id: 'technology', label: 'Technology' },
                  { id: 'healthcare', label: 'Healthcare' },
                  { id: 'finance', label: 'Finance' },
                  { id: 'consumerGoods', label: 'Consumer Goods' },
                  { id: 'manufacturing', label: 'Manufacturing' },
                  { id: 'realEstate', label: 'Real Estate' },
                  { id: 'others', label: 'Others' },
                ].map((sector) => (
                  <div key={sector.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={sector.id} 
                      checked={formData.sectorPreferences[sector.id as keyof typeof formData.sectorPreferences]} 
                      onCheckedChange={() => handleSectorToggle(sector.id)}
                    />
                    <label
                      htmlFor={sector.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {sector.label}
                    </label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Geographic Preferences */}
        <div>
          <Label className="mb-3 block">Geographic Preferences</Label>
          
          <RadioGroup 
            value={formData.geographicPreferences} 
            onValueChange={(value) => handleChange('geographicPreferences', value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="india-focused" id="india-focused" />
              <label htmlFor="india-focused" className="text-sm">India-focused</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="global-exposure" id="global-exposure" />
              <label htmlFor="global-exposure" className="text-sm">Global exposure</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="us-markets" id="us-markets" />
              <label htmlFor="us-markets" className="text-sm">US markets</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="emerging-markets" id="emerging-markets" />
              <label htmlFor="emerging-markets" className="text-sm">Emerging markets</label>
            </div>
          </RadioGroup>
        </div>
        
        {/* Active vs Passive Management Preference */}
        <div>
          <Label className="mb-3 block">Investment Management Approach</Label>
          
          <RadioGroup 
            value={formData.managementPreference} 
            onValueChange={(value) => handleChange('managementPreference', value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="active" id="active-management" />
              <label htmlFor="active-management" className="text-sm">
                Active management (fund managers actively pick stocks)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="passive" id="passive-management" />
              <label htmlFor="passive-management" className="text-sm">
                Passive management (index funds, ETFs that track markets)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="blend" id="blend-management" />
              <label htmlFor="blend-management" className="text-sm">
                Blend of both active and passive
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no-preference" id="no-preference-management" />
              <label htmlFor="no-preference-management" className="text-sm">
                No preference
              </label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </div>
  );
};

export default ProfileInvestmentPreferences;
